import { useEffect, useRef, useState, useCallback, type ReactNode, type KeyboardEvent as ReactKeyboardEvent } from 'react';
import { createPortal } from 'react-dom';
import type { AskLexAction, RewriteAction, PrivilegeLevel } from '../hooks/useNoteContextActions';

// ─── Types ──────────────────────────────────────────────────────────────────

interface NoteContextMenuProps {
  x: number;
  y: number;
  onClose: () => void;
  onAskLex: (action: AskLexAction) => void;
  onRewrite: (action: RewriteAction) => void;
  onCitationLookup: () => void;
  onCreateTask: () => void;
  onTagPrivileged: (level: PrivilegeLevel) => void;
  onAddToTimeline: () => void;
}

// ─── Viewport-aware positioning ────────────────────────────────────────────

function clampPosition(x: number, y: number, menuEl: HTMLElement | null): { left: number; top: number } {
  if (!menuEl) return { left: x, top: y };
  const rect = menuEl.getBoundingClientRect();
  const vw = window.innerWidth;
  const vh = window.innerHeight;
  let left = x;
  let top = y;
  if (x + rect.width > vw - 8) left = vw - rect.width - 8;
  if (y + rect.height > vh - 8) top = vh - rect.height - 8;
  if (left < 8) left = 8;
  if (top < 8) top = 8;
  return { left, top };
}

// ─── Icons (Heroicons outline, 16x16) ─────────────────────────────────────

function SearchIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" d="m21 21-5.197-5.197m0 0A7.5 7.5 0 1 0 5.196 5.196a7.5 7.5 0 0 0 10.607 10.607Z" />
    </svg>
  );
}

function PencilIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" d="m16.862 4.487 1.687-1.688a1.875 1.875 0 1 1 2.652 2.652L6.832 19.82a4.5 4.5 0 0 1-1.897 1.13l-2.685.8.8-2.685a4.5 4.5 0 0 1 1.13-1.897L16.863 4.487Zm0 0L19.5 7.125" />
    </svg>
  );
}

function BookOpenIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" d="M12 6.042A8.967 8.967 0 0 0 6 3.75c-1.052 0-2.062.18-3 .512v14.25A8.987 8.987 0 0 1 6 18c2.305 0 4.408.867 6 2.292m0-14.25a8.966 8.966 0 0 1 6-2.292c1.052 0 2.062.18 3 .512v14.25A8.987 8.987 0 0 0 18 18a8.967 8.967 0 0 0-6 2.292m0-14.25v14.25" />
    </svg>
  );
}

function CheckCircleIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" d="M9 12.75 11.25 15 15 9.75M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z" />
    </svg>
  );
}

function ShieldCheckIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" d="M9 12.75 11.25 15 15 9.75m-3-7.036A11.959 11.959 0 0 1 3.598 6 11.99 11.99 0 0 0 3 9.749c0 5.592 3.824 10.29 9 11.623 5.176-1.332 9-6.03 9-11.622 0-1.31-.21-2.571-.598-3.751h-.152c-3.196 0-6.1-1.248-8.25-3.285Z" />
    </svg>
  );
}

function CalendarIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" d="M6.75 3v2.25M17.25 3v2.25M3 18.75V7.5a2.25 2.25 0 0 1 2.25-2.25h13.5A2.25 2.25 0 0 1 21 7.5v11.25m-18 0A2.25 2.25 0 0 0 5.25 21h13.5A2.25 2.25 0 0 0 21 18.75m-18 0v-7.5A2.25 2.25 0 0 1 5.25 9h13.5A2.25 2.25 0 0 1 21 9v9.75" />
    </svg>
  );
}

function ChevronRightIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
    </svg>
  );
}

// ─── Sub-Menu ──────────────────────────────────────────────────────────────

function SubMenu({ parentRef, children }: { parentRef: React.RefObject<HTMLDivElement | null>; children: ReactNode }) {
  const subRef = useRef<HTMLDivElement>(null);
  const [pos, setPos] = useState<{ left: number; top: number }>({ left: 0, top: 0 });
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    if (!parentRef.current || !subRef.current) return;
    const parentRect = parentRef.current.getBoundingClientRect();
    const subRect = subRef.current.getBoundingClientRect();
    const vw = window.innerWidth;
    const vh = window.innerHeight;

    let left = parentRect.right + 2;
    let top = parentRect.top - 4;
    if (left + subRect.width > vw - 8) left = parentRect.left - subRect.width - 2;
    if (top + subRect.height > vh - 8) top = vh - subRect.height - 8;
    setPos({ left, top });
    requestAnimationFrame(() => setVisible(true));
  }, [parentRef]);

  return createPortal(
    <div
      ref={subRef}
      role="menu"
      className={`fixed z-[10000] min-w-[200px] max-w-[280px]
        bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700
        rounded-lg shadow-2xl shadow-black/20 dark:shadow-black/40 py-1
        transition-all duration-100 ease-out origin-top-left
        ${visible ? 'opacity-100 scale-100' : 'opacity-0 scale-95 pointer-events-none'}
      `}
      style={{ left: pos.left, top: pos.top }}
    >
      {children}
    </div>,
    document.body,
  );
}

// ─── Menu Item ─────────────────────────────────────────────────────────────

interface MenuItemProps {
  icon?: React.ElementType;
  label: string;
  onClick?: () => void;
  disabled?: boolean;
  children?: ReactNode;
}

function MenuItem({ icon: Icon, label, onClick, disabled, children }: MenuItemProps) {
  const [subOpen, setSubOpen] = useState(false);
  const itemRef = useRef<HTMLDivElement>(null);
  const closeTimer = useRef<ReturnType<typeof setTimeout>>(undefined);
  const hasSub = !!children;

  const handleMouseEnter = () => {
    clearTimeout(closeTimer.current);
    if (hasSub) setSubOpen(true);
  };
  const handleMouseLeave = () => {
    if (hasSub) closeTimer.current = setTimeout(() => setSubOpen(false), 200);
  };
  const handleClick = () => {
    if (disabled) return;
    if (!hasSub && onClick) onClick();
  };
  const handleKeyDown = (e: ReactKeyboardEvent) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      handleClick();
    }
    if (hasSub && (e.key === 'ArrowRight' || e.key === 'Enter')) {
      e.preventDefault();
      setSubOpen(true);
    }
    if (hasSub && e.key === 'ArrowLeft') {
      e.preventDefault();
      setSubOpen(false);
    }
  };

  return (
    <div
      ref={itemRef}
      role="menuitem"
      tabIndex={disabled ? -1 : 0}
      aria-disabled={disabled || undefined}
      onClick={handleClick}
      onKeyDown={handleKeyDown}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      className={`relative flex items-center gap-2.5 px-3 py-[7px] text-[13px] cursor-default select-none
        outline-none transition-colors duration-75
        ${disabled
          ? 'text-gray-400 dark:text-gray-500 cursor-not-allowed'
          : 'text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 focus:bg-gray-100 dark:focus:bg-gray-700'
        }`}
    >
      <span className="w-4 h-4 flex items-center justify-center shrink-0">
        {Icon && <Icon className="w-4 h-4" />}
      </span>
      <span className="flex-1">{label}</span>
      {hasSub && <ChevronRightIcon className="w-3.5 h-3.5 text-gray-400 dark:text-gray-500 ml-1" />}
      {hasSub && subOpen && <SubMenu parentRef={itemRef}>{children}</SubMenu>}
    </div>
  );
}

// ─── Separator ─────────────────────────────────────────────────────────────

function MenuSeparator() {
  return <div role="separator" className="my-1 border-t border-gray-200 dark:border-gray-700" />;
}

// ─── Root Menu ─────────────────────────────────────────────────────────────

export default function NoteContextMenu({
  x,
  y,
  onClose,
  onAskLex,
  onRewrite,
  onCitationLookup,
  onCreateTask,
  onTagPrivileged,
  onAddToTimeline,
}: NoteContextMenuProps) {
  const menuRef = useRef<HTMLDivElement>(null);
  const [pos, setPos] = useState({ left: x, top: y });
  const [visible, setVisible] = useState(false);
  const focusIndex = useRef(-1);

  useEffect(() => {
    requestAnimationFrame(() => {
      setPos(clampPosition(x, y, menuRef.current));
      setVisible(true);
    });
  }, [x, y]);

  // Close on click-outside, Escape, scroll
  useEffect(() => {
    const handleClick = (e: MouseEvent) => {
      const target = e.target as Node;
      if (menuRef.current && !menuRef.current.contains(target) && !(target as Element).closest?.('[role="menu"]')) {
        onClose();
      }
    };
    const handleKey = (e: globalThis.KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    const handleScroll = () => onClose();
    document.addEventListener('mousedown', handleClick);
    document.addEventListener('keydown', handleKey);
    window.addEventListener('scroll', handleScroll, true);
    return () => {
      document.removeEventListener('mousedown', handleClick);
      document.removeEventListener('keydown', handleKey);
      window.removeEventListener('scroll', handleScroll, true);
    };
  }, [onClose]);

  // Keyboard navigation
  const handleKeyDown = useCallback((e: ReactKeyboardEvent) => {
    const items = menuRef.current?.querySelectorAll<HTMLElement>('[role="menuitem"]:not([aria-disabled="true"])');
    if (!items?.length) return;
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      focusIndex.current = (focusIndex.current + 1) % items.length;
      items[focusIndex.current]?.focus();
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      focusIndex.current = (focusIndex.current - 1 + items.length) % items.length;
      items[focusIndex.current]?.focus();
    } else if (e.key === 'Home') {
      e.preventDefault();
      focusIndex.current = 0;
      items[0]?.focus();
    } else if (e.key === 'End') {
      e.preventDefault();
      focusIndex.current = items.length - 1;
      items[items.length - 1]?.focus();
    }
  }, []);

  // Auto-focus first item
  useEffect(() => {
    if (visible) {
      const first = menuRef.current?.querySelector<HTMLElement>('[role="menuitem"]:not([aria-disabled="true"])');
      if (first) {
        focusIndex.current = 0;
        first.focus();
      }
    }
  }, [visible]);

  return createPortal(
    <div
      ref={menuRef}
      role="menu"
      aria-label="Note actions"
      tabIndex={-1}
      onKeyDown={handleKeyDown}
      className={`fixed z-[9999] min-w-[220px] max-w-[320px]
        bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700
        rounded-lg shadow-2xl shadow-black/20 dark:shadow-black/40 py-1
        transition-all duration-150 ease-out origin-top-left
        ${visible ? 'opacity-100 scale-100' : 'opacity-0 scale-95 pointer-events-none'}
      `}
      style={{ left: pos.left, top: pos.top }}
    >
      {/* Ask Lex (with sub-menu) */}
      <MenuItem icon={SearchIcon} label="Ask Lex">
        <MenuItem label="Analyze" onClick={() => onAskLex('analyze')} />
        <MenuItem label="Counter-arguments" onClick={() => onAskLex('counterArguments')} />
        <MenuItem label="Identify Risks" onClick={() => onAskLex('identifyRisks')} />
        <MenuItem label="Plain English" onClick={() => onAskLex('plainEnglish')} />
      </MenuItem>

      {/* AI Rewrite (with sub-menu) */}
      <MenuItem icon={PencilIcon} label="AI Rewrite">
        <MenuItem label="Formalize" onClick={() => onRewrite('formalize')} />
        <MenuItem label="Simplify" onClick={() => onRewrite('simplify')} />
        <MenuItem label="Summarize" onClick={() => onRewrite('summarize')} />
        <MenuItem label="Expand" onClick={() => onRewrite('expand')} />
      </MenuItem>

      {/* Citation Lookup */}
      <MenuItem icon={BookOpenIcon} label="Citation Lookup" onClick={onCitationLookup} />

      <MenuSeparator />

      {/* Create Task */}
      <MenuItem icon={CheckCircleIcon} label="Create Task" onClick={onCreateTask} />

      {/* Tag as Privileged (with sub-menu) */}
      <MenuItem icon={ShieldCheckIcon} label="Tag as Privileged">
        <MenuItem label="Public" onClick={() => onTagPrivileged('public')} />
        <MenuItem label="Attorney Work Product" onClick={() => onTagPrivileged('attorney-work-product')} />
        <MenuItem label="Attorney-Client Privilege" onClick={() => onTagPrivileged('attorney-client-privilege')} />
      </MenuItem>

      {/* Add to Timeline */}
      <MenuItem icon={CalendarIcon} label="Add to Timeline" onClick={onAddToTimeline} />
    </div>,
    document.body,
  );
}
